import React, { useEffect, useState } from "react";
import "./fullscreen.scss";
import { eventRoomStore } from "@/stores/event";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useEventRoomState } from "@/containers/root-container";

interface FullScreenProps {
  elemId?: string;
  handlesideStream?: any;
  toggleFullScreenConfirmation?: any;
}

declare global {
  interface Document {
    mozCancelFullScreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
  }

  interface HTMLElement {
    msRequestFullscreen?: () => Promise<void>;
    mozRequestFullScreen?: () => Promise<void>;
    webkitRequestFullscreen?: () => Promise<void>;
  }
}

const FullScreen: React.FC<FullScreenProps> = ({
  elemId,
  handlesideStream,
  toggleFullScreenConfirmation,
}) => {
  const [screenMode, setScreenMode] = useState("exit");
  const eventRoomState = useEventRoomState();

  useEffect(() => {
    if (
      (screenMode == "exit" && eventRoomState.me.info.role == "residence") ||
      (screenMode == "exit" && eventRoomState.me.info.role == "multiconnection")
    ) {
      const timer = setTimeout(() => {
        toggleFullScreenConfirmation();
      }, 180000);
      return () => clearTimeout(timer);
    }
  }, [screenMode]);

  // registering full screen change event listener
  useEffect(() => {
    /* Standard syntax */
    document.addEventListener("fullscreenchange", handleESCKeyPress);

    /* Firefox */
    document.addEventListener("mozfullscreenchange", handleESCKeyPress);

    /* Chrome, Safari and Opera */
    document.addEventListener("webkitfullscreenchange", handleESCKeyPress);

    /* IE / Edge */
    document.addEventListener("msfullscreenchange", handleESCKeyPress);

    return () => {
      document.removeEventListener("fullscreenchange", handleESCKeyPress);
      document.removeEventListener("mozfullscreenchange", handleESCKeyPress);
      document.removeEventListener("webkitfullscreenchange", handleESCKeyPress);
      document.removeEventListener("msfullscreenchange", handleESCKeyPress);
    };
  }, []);

  const onFullScreenClick = (action: string) => {
    action === "enter" ? goToFullScreenMode() : ExitFullScreenMode();
  };

  const goToFullScreenMode = () => {
    const elem =
      (elemId && document.getElementById(elemId)) || document.documentElement;
    try {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        /* Firefox */
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        /* Chrome, Safari & Opera */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        /* IE/Edge */
        elem.msRequestFullscreen();
      }
    } catch (err) {}
    handlesideStream(true);
  };

  const ExitFullScreenMode = () => {
    try {
      let resp = eventRoomStore.ExitFullScreenMode();
      if (resp == "success") {
        handlesideStream(false);
      }
    } catch (err) {}
  };

  const handleESCKeyPress = () => {
    let mainDiv = document.querySelector(".preshow-talk") as any;
    if (mainDiv.classList.contains("full-screen")) {
      handlesideStream(false);
    } else {
      handlesideStream(false);
    }

    mainDiv.classList.toggle("full-screen");
    let rightSideElement = document.querySelector(
      "section.sidebar-container._right"
    ) as any;
    if (rightSideElement != null) {
      rightSideElement.classList.toggle("float-chat-right");
    }

    setScreenMode((prevState) => (prevState === "enter" ? "exit" : "enter"));
  };

  return (
    <div
      className="expand"
      onClick={() =>
        onFullScreenClick(screenMode === "enter" ? "exit" : "enter")
      }
    >
      <span className="expand-icon">
        <div className="expand-position">
          {screenMode === "enter" ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </div>
      </span>
      {eventRoomState.me.info.role == "guide" ? (
        <p className="icons-tip">
          {screenMode === "enter" ? "Exit Full Screen" : "Full Screen"}
        </p>
      ) : (
        <p className="icons-tip">
          {screenMode === "enter" ? "Réduire l'écran" : "Plein écran"}
        </p>
      )}
    </div>
  );
};

export default React.memo(FullScreen);
